import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from 'react';
import {
  Dialog, Box, Typography, IconButton, Button, Tooltip, Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import CloseIcon from '@material-ui/icons/Close';
import PhoneIcon from '@material-ui/icons/Phone';
import ChatBubbleIcon from '@material-ui/icons/ChatBubble';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonIcon from '@material-ui/icons/Person';
import ScheduleIcon from '@material-ui/icons/Schedule';
import HistoryIcon from '@material-ui/icons/History';
import EditIcon from '@material-ui/icons/Edit';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import RoomIcon from '@material-ui/icons/Room';
import GpsFixedIcon from '@material-ui/icons/GpsFixed';
import GpsNotFixedIcon from '@material-ui/icons/GpsNotFixed';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import { GoogleMap, useJsApiLoader, Marker, Polyline } from '@react-google-maps/api';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { AuthContext } from '../../App';
import ChatDriverDialog from './ChatDriverDialog';
import { otLabel, otTooltip } from '../../utils/otLabel';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const LIBRARIES = ['places'];

// Live vehicle marker — red filled CIRCLE. Switched from FORWARD_CLOSED_ARROW
// because the rotation never updates reliably (Google's symbol cache + course
// data quality) and a static arrow pointing the wrong way is worse UX than a
// neutral circle. Course is irrelevant for a circle, so it's not used.
function buildLiveVehicleIcon(/* course unused */) {
  if (!window.google?.maps) return undefined;
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale: 9,
    fillColor: '#dc2626',
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 3,
  };
}

// Numbered waypoint pin — shows the sequence number of each OT delivery on
// the map, so the operator can scan the sequence visually. Highlighted when
// the OT is the currently-selected leg.
function buildWaypointPinIcon({ index, color = '#2563eb', selected = false }) {
  if (!window.google?.maps) return null;
  const w = 30, h = 38;
  const fill = selected ? '#f97316' : color;
  const ring = selected ? '#9a3412' : '#0b1220';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 30 38">
    <defs><filter id="wpsh" x="-20%" y="-10%" width="140%" height="125%">
      <feDropShadow dx="0" dy="1" stdDeviation="1.0" flood-color="#0b1220" flood-opacity="0.30"/>
    </filter></defs>
    <g filter="url(#wpsh)">
      <path d="M15 0C7.27 0 1 6.27 1 14c0 10.5 14 24 14 24s14-13.5 14-24C29 6.27 22.73 0 15 0z" fill="${fill}" stroke="${ring}" stroke-width="0.6" stroke-opacity="0.4"/>
      <circle cx="15" cy="13" r="9" fill="#ffffff"/>
      <text x="15" y="13.6" text-anchor="middle" dominant-baseline="central"
        font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif"
        font-weight="800" font-size="12" fill="${fill}">${index}</text>
    </g>
  </svg>`;
  return { url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg), w, h };
}

// Plate-label balloon — same visual style as the Frota map (rounded dark pill
// with downward tail). Rendered as a SEPARATE marker so the rotation of the
// arrow doesn't drag the plate sideways. Sits above the vehicle position.
function buildPlateLabelIcon(plate) {
  if (!window.google?.maps) return null;
  const text = (plate || '').toUpperCase();
  if (!text) return null;
  const charW = 6;
  const padX = 8;
  const bubbleW = Math.max(48, Math.min(110, padX * 2 + text.length * charW));
  const bubbleH = 16;
  const tailH = 6;
  const tailW = 7;
  const pad = 2;
  const svgW = bubbleW + pad * 2;
  const svgH = bubbleH + tailH + pad * 2;
  const bubbleX = pad;
  const bubbleY = pad;
  const tailCx = svgW / 2;
  const tailTop = bubbleY + bubbleH;
  const tailBottom = tailTop + tailH;
  const tailLeft = tailCx - tailW / 2;
  const tailRight = tailCx + tailW / 2;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}">
<defs>
<filter id="lps" x="-20%" y="-20%" width="140%" height="160%">
<feDropShadow dx="0" dy="0.8" stdDeviation="1.2" flood-color="#000" flood-opacity="0.28"/>
</filter>
</defs>
<g filter="url(#lps)">
<rect x="${bubbleX}" y="${bubbleY}" width="${bubbleW}" height="${bubbleH}" rx="4" ry="4" fill="#0f172a" opacity="0.92"/>
<path d="M${tailLeft} ${tailTop} L${tailCx} ${tailBottom} L${tailRight} ${tailTop}Z" fill="#0f172a" opacity="0.92"/>
</g>
<text x="${svgW / 2}" y="${bubbleY + bubbleH / 2 + 0.5}" text-anchor="middle" dominant-baseline="central" font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif" font-size="9" font-weight="700" fill="#ffffff" letter-spacing="0.4">${text}</text>
</svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    w: svgW, h: svgH,
  };
}

// Endpoint pin (small teardrop). Same look as the planner map.
function buildEndpointIcon({ color, glyph }) {
  const w = 26, h = 34;
  const pin = 'M17 0C7.61 0 0 7.61 0 17c0 12.75 17 27 17 27s17-14.25 17-27C34 7.61 26.39 0 17 0z';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 34 44">
    <defs><filter id="sh" x="-20%" y="-10%" width="140%" height="125%">
      <feDropShadow dx="0" dy="1" stdDeviation="0.9" flood-color="#0f172a" flood-opacity="0.35"/>
    </filter></defs>
    <g filter="url(#sh)">
      <path d="${pin}" fill="${color}"/>
      <circle cx="17" cy="15" r="9.5" fill="#ffffff"/>
      <text x="17" y="15.6" text-anchor="middle" dominant-baseline="central"
        font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Arial,sans-serif"
        font-weight="800" font-size="13" fill="${color}">${glyph}</text>
    </g>
  </svg>`;
  return { url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg), w, h };
}

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      maxWidth: '95vw',
      width: '95vw',
      height: '95vh',
      maxHeight: '95vh',
      // Pull the dialog up a few pixels so it sits closer to the top edge of
      // the viewport (operator gets more vertical room without going fullscreen).
      marginTop: '2vh !important',
      alignSelf: 'flex-start',
      overflow: 'hidden',
      margin: 0,
      // Make the paper a flex column so the body actually expands to fill the
      // space between the fixed header and footer — without this `flex: 1` on
      // `.body` is a no-op and the bottom cards/history get clipped.
      display: 'flex',
      flexDirection: 'column',
    },
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 20px', height: 56,
    flex: '0 0 auto',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
  },
  headerLive: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '3px 9px', borderRadius: 999,
    background: '#fef2f2', border: '1px solid #fecaca',
    color: '#dc2626', fontSize: '0.66rem', fontWeight: 800,
    letterSpacing: '0.06em', textTransform: 'uppercase',
  },
  liveDot: {
    width: 7, height: 7, borderRadius: '50%', background: '#dc2626',
    animation: '$pulse 1.5s ease-out infinite',
  },
  '@keyframes pulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(220,38,38,0.6)' },
    '70%': { boxShadow: '0 0 0 8px rgba(220,38,38,0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(220,38,38,0)' },
  },
  // Three-zone cockpit layout: top row [leftCol | map | rightCol] + a sticky
  // history strip at the bottom that's ALWAYS visible (no longer buried inside
  // a scrollable info column). Operator-facing KPIs hug the corners while the
  // truck moves on a large central map.
  body: {
    display: 'flex', flex: 1, minHeight: 0,
    background: '#f1f5f9',
    flexDirection: 'column',
  },
  bodyTop: {
    display: 'flex', flex: '1 1 auto', minHeight: 0,
    flexDirection: 'row',
  },
  leftCol: {
    flex: '0 0 300px', minHeight: 0, overflowY: 'auto',
    padding: '12px',
    background: '#fff', borderRight: '1px solid #e2e8f0',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  mapPane: { flex: '1 1 auto', minWidth: 0, minHeight: 0, position: 'relative' },
  rightCol: {
    flex: '0 0 320px', minHeight: 0, overflowY: 'auto',
    padding: '12px',
    background: '#fff', borderLeft: '1px solid #e2e8f0',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  bottomCard: {
    background: '#fff', borderRadius: 10,
    border: '1px solid #e2e8f0', padding: '10px 12px',
  },
  // Sticky bottom strip — split into two halves: history on the left, leg
  // details (the currently-selected OT) on the right. Always visible regardless
  // of how much the operator scrolls inside the columns above. Height is
  // controlled at runtime via inline style + drag handle.
  historyStrip: {
    flexShrink: 0,
    borderTop: '1px solid #e2e8f0',
    background: '#f8fafc',
    overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    position: 'relative',
  },
  stripDragHandle: {
    height: 8, flexShrink: 0,
    cursor: 'ns-resize',
    background: 'linear-gradient(180deg, transparent, #e2e8f0 50%, transparent)',
    borderTop: '1px solid #e2e8f0',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    userSelect: 'none',
    '&:hover': { background: 'linear-gradient(180deg, transparent, #94a3b8 50%, transparent)' },
  },
  stripDragGrip: {
    width: 36, height: 3, borderRadius: 999,
    background: '#94a3b8',
  },
  stripBody: {
    flex: 1, minHeight: 0,
    display: 'flex', flexDirection: 'row',
    overflow: 'hidden',
  },
  stripCollapsedNote: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.66rem', color: '#94a3b8', fontStyle: 'italic',
    background: '#fff',
  },
  historyHalf: {
    flex: '1 1 50%', minWidth: 0, minHeight: 0,
    background: '#fff',
    borderRight: '1px solid #e2e8f0',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  legHalf: {
    flex: '1 1 50%', minWidth: 0, minHeight: 0,
    background: '#fff',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  historyPane: {
    flex: 1, minHeight: 0, overflowY: 'auto',
  },
  legPane: {
    flex: 1, minHeight: 0, overflowY: 'auto',
    padding: '6px 12px 10px',
  },
  legHeader: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 12px',
    background: 'linear-gradient(180deg, #f8fafc, #f1f5f9)',
    borderBottom: '1px solid #e2e8f0',
  },
  historyHeader: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 10px',
    background: 'linear-gradient(180deg, #f8fafc, #f1f5f9)',
    borderBottom: '1px solid #e2e8f0',
    position: 'sticky', top: 0, zIndex: 1,
  },
  historyTable: {
    width: '100%', borderCollapse: 'collapse',
    fontSize: '0.66rem',
  },
  historyRow: {
    '& td': {
      padding: '4px 10px',
      borderBottom: '1px solid #f1f5f9',
      verticalAlign: 'top',
    },
    '&:hover': { background: '#fafbfc' },
  },
  card: {
    background: '#fff', borderRadius: 10,
    border: '1px solid #e2e8f0', padding: 12,
  },
  cardTitle: {
    fontSize: '0.62rem', fontWeight: 800, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 8,
    display: 'flex', alignItems: 'center', gap: 6,
  },
  driverHeader: {
    display: 'flex', alignItems: 'center', gap: 12,
    marginBottom: 10,
  },
  driverAvatar: {
    width: 44, height: 44, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
    border: '1px solid #cbd5e1',
    fontSize: '0.95rem', fontWeight: 800, color: '#475569',
  },
  driverName: { fontSize: '0.92rem', fontWeight: 700, color: '#0f172a' },
  driverPhone: { fontSize: '0.72rem', color: '#64748b', marginTop: 2 },
  // Discrete circular chat button in the driver header — sits next to the
  // avatar and stays visually subordinate to the primary Ligar/WhatsApp pair.
  driverChatBtn: {
    width: 34, height: 34,
    borderRadius: 999,
    background: 'linear-gradient(135deg, #eef2ff, #e0e7ff) !important',
    color: '#4338ca',
    border: '1px solid #c7d2fe',
    flexShrink: 0,
    transition: 'transform 0.12s, box-shadow 0.12s',
    '&:hover': {
      background: 'linear-gradient(135deg, #6366f1, #4f46e5) !important',
      color: '#fff',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 10px rgba(99,102,241,0.35)',
    },
  },
  contactRow: { display: 'flex', gap: 8 },
  contactBtn: {
    flex: 1, padding: '8px 12px', fontSize: '0.72rem', fontWeight: 700,
    borderRadius: 8, textTransform: 'none',
    color: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  vehicleRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 10px', background: '#f8fafc', borderRadius: 8,
  },
  vehiclePlate: { fontSize: '0.86rem', fontWeight: 800, color: '#0f172a' },
  vehicleSub: { fontSize: '0.66rem', color: '#64748b' },

  otRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 10px', borderRadius: 8,
    cursor: 'pointer',
    border: '1px solid transparent',
    transition: 'background 0.15s, border-color 0.15s',
    '&:hover': { background: '#f8fafc', borderColor: '#e2e8f0' },
  },
  otRowActive: { background: '#eff6ff !important', borderColor: '#bfdbfe !important' },
  otBadge: {
    width: 22, height: 22, borderRadius: 6, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.62rem', fontWeight: 800,
  },
  otName: { fontSize: '0.74rem', fontWeight: 700, color: '#0f172a' },
  otSub: { fontSize: '0.62rem', color: '#64748b' },

  footer: {
    padding: '12px 20px',
    borderTop: '1px solid #e2e8f0',
    flex: '0 0 auto',
    display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end',
    background: '#fff',
  },
  actionBtn: {
    fontSize: '0.78rem', fontWeight: 600, textTransform: 'none',
    borderRadius: 8, padding: '7px 14px',
  },
}));

// Status colour map — matches the colours used elsewhere in OrdersView
const STATUS_CFG = {
  pending:    { color: '#94a3b8', bg: '#f1f5f9', label: 'Pendente' },
  assigned:   { color: '#3b82f6', bg: '#eff6ff', label: 'Atribuída' },
  in_transit: { color: '#f59e0b', bg: '#fffbeb', label: 'Em Trânsito' },
  delivered:  { color: '#22c55e', bg: '#f0fdf4', label: 'Entregue' },
  cancelled:  { color: '#ef4444', bg: '#fef2f2', label: 'Cancelada' },
};

function initials(name) {
  return (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function LiveRoutePanel({
  open, onClose, order, allOrders = [], drivers = [],
  routeGeometry = null, // { coordinates: [[lng,lat], ...] } from route.computedRoute.geometry
  onOpenOT, // (orderId) — switch to a different OT inside the panel
  onContactDriver, // (order)
  onEditRoute, // (order)
  onOpenHistory, // (order)
  onStatusChange, // (order, newStatus)
  onStartSimulation, // (order) — start a route simulation so the operator sees the truck moving
  onAddOrderToRoute, // (currentOrder, newOrderToAdd) — add a pending OT to this route
  clientSettings = null, // used to gate the tachograph card (only for clients with useTachograph=true)
  routeMeta = null, // optional { totalDistanceM, totalDurationS, departureTime } from the route record
  lang = 'pt',
}) {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const vehicles = useSelector(selectVehicles);
  const mapRef = useRef(null);
  const [vehiclePos, setVehiclePos] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [followVehicle, setFollowVehicle] = useState(true);
  const initialFitDoneRef = useRef(false);
  // Selected leg / OT — controls the right half of the bottom strip and the
  // highlighted polyline overlay on the map. Defaults to the OT the panel was
  // opened for. Reset when the panel re-opens for a different order.
  const [selectedOTId, setSelectedOTId] = useState(order ? (order._id || order.id) : null);
  useEffect(() => {
    if (open && order) setSelectedOTId(order._id || order.id);
  }, [open, order?._id, order?.id]);

  // Resizable bottom strip — operator can drag the top edge up/down to give
  // more room to the map (collapse the strip) or to the cards/history (grow
  // it). Persisted to localStorage so the choice sticks across sessions.
  const STRIP_KEY = 'ecoplanner_live_strip_h';
  const [stripHeight, setStripHeight] = useState(() => {
    const saved = parseInt(localStorage.getItem(STRIP_KEY), 10);
    if (Number.isFinite(saved) && saved >= 60 && saved <= 600) return saved;
    return 240;
  });
  const dragRef = useRef(null);
  const onDragStart = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = stripHeight;
    dragRef.current = { h: startH };
    // Lock the cursor and disable text selection during the drag so the
    // operator can move freely without the browser highlighting things.
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      const dy = startY - ev.clientY;
      const nextH = Math.max(60, Math.min(600, startH + dy));
      dragRef.current.h = nextH;
      setStripHeight(nextH);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('blur', onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
      try { localStorage.setItem(STRIP_KEY, String(dragRef.current?.h ?? stripHeight)); } catch { /* ignore */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    // If the window loses focus mid-drag (alt-tab etc.) we still release the
    // listeners — otherwise the cursor stays stuck in ns-resize when the user
    // returns. Same defensive cleanup as a "mouse left page" guard.
    window.addEventListener('blur', onUp);
  };
  const stripCollapsed = stripHeight <= 70;
  const toggleStrip = () => {
    const next = stripCollapsed ? 240 : 60;
    setStripHeight(next);
    try { localStorage.setItem(STRIP_KEY, String(next)); } catch { /* ignore */ }
  };

  // "Add order to route" picker — opens a small dialog listing pending OTs of
  // the same service type that the operator can attach to the current route.
  // Confirming hands the new OT to the parent (OrdersView) which assigns the
  // same vehicle/driver and rebuilds the consolidated route, then opens the
  // planner in replan mode for a final confirmation.
  const [addOrderOpen, setAddOrderOpen] = useState(false);
  const [addOrderSearch, setAddOrderSearch] = useState('');
  // Driver chat dialog — opens when the operator clicks "Chat Motorista".
  const [chatOpen, setChatOpen] = useState(false);
  // Inline OT history — fetched once when the panel opens. Replaces the old
  // separate history dialog by rendering events as a compact table below the
  // info strip, so the operator stays in the live screen.
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  useEffect(() => {
    if (!open || !order || !token) return;
    const oid = order._id || order.id;
    if (!oid) return;
    setHistoryLoading(true);
    fetch(`/planner-api/transport-orders/${oid}/history`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { events: [] })
      .then(data => setHistoryEvents(Array.isArray(data?.events) ? data.events : []))
      .catch(() => setHistoryEvents([]))
      .finally(() => setHistoryLoading(false));
  }, [open, order?._id, order?.id, token]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
    language: 'pt',
    region: 'PT',
  });

  // Live vehicle position — pulled from the vehicles slice (which is updated
  // via WS `vehicles:update` and `vehicle:simulated` events). We match by
  // vehicleId first (most precise) and fall back to plate so a vehicle
  // re-assigned via SQL still resolves correctly.
  // Heading: prefer the GPS-reported `course`. When it's missing/0 (typical
  // for parked or slow vehicles) we fall back to a heading derived from the
  // last two known positions — keeps the arrow pointed in the direction of
  // travel even when Traccar reports stale data.
  const lastPosRef = useRef(null);
  useEffect(() => {
    if (!order) { setVehiclePos(null); lastPosRef.current = null; return; }
    let v = null;
    if (order.vehicleId) {
      v = vehicles.find(x => Number(x.id) === Number(order.vehicleId));
    }
    if (!v && order.vehiclePlate) {
      v = vehicles.find(x => (x.plate || '').toUpperCase() === order.vehiclePlate.toUpperCase());
    }
    if (v?.position?.latitude != null && v.position.longitude != null) {
      const lat = Number(v.position.latitude);
      const lng = Number(v.position.longitude);
      const reportedCourse = Number(v.position.course ?? v.position.heading);
      let heading = Number.isFinite(reportedCourse) && reportedCourse !== 0
        ? reportedCourse
        : null;
      if (heading == null && lastPosRef.current) {
        const prev = lastPosRef.current;
        const dLat = lat - prev.lat;
        const dLng = (lng - prev.lng) * Math.cos((lat + prev.lat) / 2 * Math.PI / 180);
        const dist2 = dLat * dLat + dLng * dLng;
        // Only update heading when we've moved at least ~3m to avoid jitter.
        if (dist2 > 1e-9) {
          heading = (Math.atan2(dLng, dLat) * 180 / Math.PI + 360) % 360;
        }
      }
      // Preserve previous heading if we couldn't compute a new one (vehicle
      // didn't move enough this tick) — avoids the arrow snapping to north.
      const finalHeading = heading != null
        ? heading
        : (lastPosRef.current?.heading ?? 0);
      lastPosRef.current = { lat, lng, heading: finalHeading };
      setVehiclePos({
        latitude: lat,
        longitude: lng,
        speed: v.position.speed,
        course: finalHeading,
        fixTime: v.position.fixTime,
        simulated: !!v._simulating,
      });
    } else {
      setVehiclePos(null);
    }
  }, [order, order?.vehicleId, order?.vehiclePlate, vehicles]);

  // Sibling OTs on the same route (only meaningful for consolidated routes)
  const siblingOTs = useMemo(() => {
    if (!order?.assignedRouteId) return [order].filter(Boolean);
    const sibs = allOrders.filter(o => o.assignedRouteId === order.assignedRouteId);
    if (sibs.length === 0) return [order];
    // Sort by delivery window (matches the consolidator's order)
    return [...sibs].sort((a, b) => {
      const ta = new Date(a.deliveryWindowFrom || a.pickupWindowFrom || a.createdAt || 0).getTime();
      const tb = new Date(b.deliveryWindowFrom || b.pickupWindowFrom || b.createdAt || 0).getTime();
      return ta - tb;
    });
  }, [order, allOrders]);

  // Driver record (full details) — looked up by id, with fallbacks for the cached fields on the OT
  const driver = useMemo(() => {
    if (order?.driverId) {
      const d = drivers.find(x => Number(x.id) === Number(order.driverId));
      if (d) return d;
    }
    return {
      name: order?.driverName || null,
      phone: order?.driverPhone || null,
      email: null,
    };
  }, [order, drivers]);

  // Resolve the currently-selected OT (the one whose details + leg highlight
  // we render). Falls back to the panel's primary order if nothing was clicked.
  const selectedOT = useMemo(() => {
    if (!selectedOTId) return order;
    return siblingOTs.find(o => Number(o._id || o.id) === Number(selectedOTId)) || order;
  }, [selectedOTId, siblingOTs, order]);

  // Origin / destination / waypoints from the route geometry (when available)
  // OR fall back to the SELECTED OT's own pickup/delivery for non-consolidated
  // routes. (For consolidated routes, the route geometry already includes all
  // legs; we use route.origin/destination + the OT's pickup/delivery for the
  // markers shown.)
  const mapPoints = useMemo(() => {
    const path = (routeGeometry?.coordinates || []).map(c => ({ lat: c[1], lng: c[0] }));
    const pickup = selectedOT?.pickupLat && selectedOT?.pickupLng
      ? { lat: Number(selectedOT.pickupLat), lng: Number(selectedOT.pickupLng), name: selectedOT.pickupName || selectedOT.pickupAddress }
      : null;
    const delivery = selectedOT?.deliveryLat && selectedOT?.deliveryLng
      ? { lat: Number(selectedOT.deliveryLat), lng: Number(selectedOT.deliveryLng), name: selectedOT.deliveryName || selectedOT.deliveryAddress }
      : null;
    return { path, pickup, delivery };
  }, [routeGeometry, selectedOT]);

  // Per-OT leg distance (in km) — for each sibling OT, computes the haversine
  // distance from the previous stop to that OT's delivery, walking the route
  // polyline. Returns a Map keyed by OT id. Origin-of-route → first OT counts
  // as that OT's leg; subsequent OTs count from previous OT's delivery.
  const legKmByOTId = useMemo(() => {
    const out = new Map();
    const coords = routeGeometry?.coordinates || [];
    if (coords.length < 2 || siblingOTs.length === 0) return out;
    const haversineKm = (a, b) => {
      const R = 6371;
      const toRad = d => d * Math.PI / 180;
      const dLat = toRad(b[1] - a[1]);
      const dLng = toRad(b[0] - a[0]);
      const lat1 = toRad(a[1]), lat2 = toRad(b[1]);
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(h));
    };
    const nearestIdx = (lng, lat) => {
      let best = 0, bestD = Infinity;
      for (let i = 0; i < coords.length; i++) {
        const dx = coords[i][0] - lng;
        const dy = coords[i][1] - lat;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = i; }
      }
      return best;
    };
    // Cumulative km along the polyline at each index, computed once.
    const cum = new Array(coords.length);
    cum[0] = 0;
    for (let i = 1; i < coords.length; i++) {
      cum[i] = cum[i - 1] + haversineKm(coords[i - 1], coords[i]);
    }
    let prevIdx = 0;
    for (const ot of siblingOTs) {
      const lat = Number(ot.deliveryLat);
      const lng = Number(ot.deliveryLng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const idx = nearestIdx(lng, lat);
      const km = Math.max(0, cum[idx] - cum[prevIdx]);
      out.set(Number(ot._id || ot.id), km);
      prevIdx = idx;
    }
    return out;
  }, [routeGeometry, siblingOTs]);

  // Highlighted leg polyline — when an OT is selected we slice the route
  // geometry between (pickup → delivery) of that OT and render it in orange
  // on top of the base blue route. Mirrors the same approach used by
  // MapViewGoogle.js#otLegPath (nearest-index slicing on the polyline).
  const highlightedLegPath = useMemo(() => {
    if (!selectedOT || siblingOTs.length < 2) return null;
    const coords = routeGeometry?.coordinates || [];
    if (coords.length < 2) return null;
    const pickupCoord = (selectedOT.pickupLat && selectedOT.pickupLng)
      ? [Number(selectedOT.pickupLng), Number(selectedOT.pickupLat)] : null;
    const deliveryCoord = (selectedOT.deliveryLat && selectedOT.deliveryLng)
      ? [Number(selectedOT.deliveryLng), Number(selectedOT.deliveryLat)] : null;
    if (!pickupCoord || !deliveryCoord) return null;
    // For an assistance route the "pickup" of every OT equals the technician's
    // home — the leg the operator cares about is really the previous-stop →
    // delivery segment. So if pickup is shared across siblings, fall back to
    // the previous OT's delivery as the leg start.
    const myIdx = siblingOTs.findIndex(o => Number(o._id || o.id) === Number(selectedOT._id || selectedOT.id));
    let legStart = pickupCoord;
    if (myIdx > 0) {
      const prev = siblingOTs[myIdx - 1];
      if (prev?.deliveryLat && prev?.deliveryLng) {
        legStart = [Number(prev.deliveryLng), Number(prev.deliveryLat)];
      }
    }
    const nearestIdx = (c) => {
      let best = 0, bestD = Infinity;
      for (let i = 0; i < coords.length; i++) {
        const dx = coords[i][0] - c[0];
        const dy = coords[i][1] - c[1];
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = i; }
      }
      return best;
    };
    const a = nearestIdx(legStart);
    const b = nearestIdx(deliveryCoord);
    const [from, to] = a <= b ? [a, b] : [b, a];
    if (to - from < 2) return null;
    return coords.slice(from, to + 1).map(c => ({ lat: c[1], lng: c[0] }));
  }, [selectedOT, siblingOTs, routeGeometry]);

  // Fit bounds ONCE when the panel opens with route geometry. After that, we
  // pan to follow the vehicle (if `followVehicle` is on) without ever calling
  // fitBounds again — that would jerk the camera every WS tick.
  const onLoadMap = useCallback((map) => {
    mapRef.current = map;
    initialFitDoneRef.current = false;
  }, []);
  // First fit triggers when the geometry is loaded. We re-evaluate as soon as
  // ANY of (path, pickup, delivery, vehiclePos) becomes available — guarantees
  // the operator opens the panel and immediately sees the full route AND the
  // vehicle (instead of a tight zoom on the origin or only the route).
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    const hasGeometry = mapPoints.path.length > 1 || mapPoints.pickup || mapPoints.delivery;
    const hasVehicle = !!vehiclePos;
    if (!hasGeometry && !hasVehicle) return;
    // Skip when already fitted with both pieces — preserves the operator's
    // manual zoom/pan after the initial frame.
    if (initialFitDoneRef.current) return;

    const b = new window.google.maps.LatLngBounds();
    let added = false;
    mapPoints.path.forEach(p => { b.extend(p); added = true; });
    if (mapPoints.pickup) { b.extend(mapPoints.pickup); added = true; }
    if (mapPoints.delivery) { b.extend(mapPoints.delivery); added = true; }
    // Add OT delivery points (numbered pins) so they're framed too.
    siblingOTs.forEach(o => {
      const lat = Number(o.deliveryLat);
      const lng = Number(o.deliveryLng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        b.extend({ lat, lng });
        added = true;
      }
    });
    if (vehiclePos) {
      b.extend({ lat: vehiclePos.latitude, lng: vehiclePos.longitude });
      added = true;
    }
    if (added) {
      mapRef.current.fitBounds(b, { top: 60, bottom: 60, left: 60, right: 60 });
      // Mark fit as done only when we had both geometry AND vehicle (or one of
      // them was definitively unavailable). Otherwise, allow a re-fit when the
      // missing piece arrives moments later.
      if (hasGeometry && (hasVehicle || !order?.vehicleId)) {
        initialFitDoneRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, mapPoints, vehiclePos, siblingOTs]);

  // Follow the vehicle: pan to its position whenever it changes. Only active
  // when `followVehicle` is true — the operator can disable it with the
  // crosshair toggle to inspect other parts of the map without being yanked.
  // While a simulation is running we explicitly DO NOT auto-pan: the operator
  // is using the live screen as a presentation tool, panning every 2s on the
  // simulated marker is disorienting. The operator can still re-center
  // manually via "Enquadrar toda a rota" or by toggling follow back on.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !vehiclePos || !followVehicle) return;
    if (vehiclePos.simulated) return;
    if (!initialFitDoneRef.current) return; // wait for the first fit
    mapRef.current.panTo({ lat: vehiclePos.latitude, lng: vehiclePos.longitude });
    // Keep the zoom comfortable for street-level view if the operator hasn't
    // adjusted it (we don't force, just suggest a minimum).
    const z = mapRef.current.getZoom();
    if (z != null && z < 13) mapRef.current.setZoom(15);
  }, [isLoaded, vehiclePos?.latitude, vehiclePos?.longitude, followVehicle, vehiclePos?.simulated]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!order) return null;

  const status = order.status || 'pending';
  const statusCfg = STATUS_CFG[status] || STATUS_CFG.pending;
  const isFinal = status === 'delivered' || status === 'cancelled';
  const phoneDigits = (driver.phone || '').replace(/[^0-9+]/g, '');

  const originIcon = isLoaded ? buildEndpointIcon({ color: '#16a34a', glyph: '⚐' }) : null;
  const destIcon = isLoaded ? buildEndpointIcon({ color: '#dc2626', glyph: '⚑' }) : null;

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} fullWidth>
      {/* HEADER ─────────────────────────────────────────────── */}
      <Box className={classes.header}>
        <span className={classes.headerLive}>
          <span className={classes.liveDot} /> Live
        </span>
        <Typography style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>
          {otLabel(order)}
        </Typography>
        <Chip size="small" label={statusCfg.label}
          style={{ background: statusCfg.bg, color: statusCfg.color, fontWeight: 700, fontSize: '0.66rem' }}
        />
        {siblingOTs.length > 1 && (
          <Chip size="small" label={`Rota com ${siblingOTs.length} ordens`}
            style={{ background: '#eef2ff', color: '#4338ca', fontWeight: 700, fontSize: '0.66rem' }}
          />
        )}
        <Box style={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}>
          <CloseIcon style={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* BODY ───────────────────────────────────────────────── */}
      <Box className={classes.body}>
        {/* TOP ZONE — leftCol | map | rightCol. The left column is ordered
            visually first via flex `order` even though it appears after the
            map in the DOM (keeps the JSX edit surface small). */}
        <Box className={classes.bodyTop}>
        {/* MAP */}
        <Box className={classes.mapPane}>
          {/* Floating controls — top-left of the map. Auto-follow toggle and
              quick "fit all" button to re-frame the whole route. */}
          {isLoaded && (
            <Box style={{
              position: 'absolute', top: 12, left: 12, zIndex: 10,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <Tooltip title={followVehicle ? 'Seguir veículo · activo' : 'Seguir veículo · desligado'}>
                <Box
                  onClick={() => setFollowVehicle(v => !v)}
                  style={{
                    width: 38, height: 38, borderRadius: 10,
                    background: followVehicle ? '#2563eb' : '#fff',
                    color: followVehicle ? '#fff' : '#475569',
                    border: `1px solid ${followVehicle ? '#1d4ed8' : '#e2e8f0'}`,
                    boxShadow: '0 2px 8px rgba(15,23,42,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}>
                  {followVehicle
                    ? <GpsFixedIcon style={{ fontSize: 18 }} />
                    : <GpsNotFixedIcon style={{ fontSize: 18 }} />}
                </Box>
              </Tooltip>
              <Tooltip title="Enquadrar toda a rota">
                <Box
                  onClick={() => {
                    if (!mapRef.current) return;
                    const b = new window.google.maps.LatLngBounds();
                    let added = false;
                    mapPoints.path.forEach(p => { b.extend(p); added = true; });
                    if (mapPoints.pickup) { b.extend(mapPoints.pickup); added = true; }
                    if (mapPoints.delivery) { b.extend(mapPoints.delivery); added = true; }
                    if (vehiclePos) { b.extend({ lat: vehiclePos.latitude, lng: vehiclePos.longitude }); added = true; }
                    if (added) mapRef.current.fitBounds(b, { top: 50, bottom: 50, left: 50, right: 50 });
                    setFollowVehicle(false);
                  }}
                  style={{
                    width: 38, height: 38, borderRadius: 10,
                    background: '#fff', color: '#475569',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 2px 8px rgba(15,23,42,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}>
                  <CenterFocusStrongIcon style={{ fontSize: 18 }} />
                </Box>
              </Tooltip>
            </Box>
          )}
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={vehiclePos
                ? { lat: vehiclePos.latitude, lng: vehiclePos.longitude }
                : (mapPoints.pickup || mapPoints.delivery || { lat: 41.16, lng: -8.62 })}
              zoom={13}
              onLoad={onLoadMap}
              options={{
                gestureHandling: 'greedy',
                streetViewControl: true,
                mapTypeControl: false,
                fullscreenControl: false,
                clickableIcons: false,
              }}
            >
              {/* Highlighted leg polyline — drawn UNDER the base polyline so
                  the orange ribbon glows around the selected segment but the
                  blue line remains continuous on top. */}
              {highlightedLegPath && highlightedLegPath.length > 1 && (
                <Polyline path={highlightedLegPath} options={{
                  strokeColor: '#f97316', strokeOpacity: 1, strokeWeight: 7,
                  geodesic: true, clickable: false, zIndex: 3,
                }} />
              )}

              {/* Route polyline (full geometry from HERE) */}
              {mapPoints.path.length > 1 && (
                <>
                  <Polyline path={mapPoints.path} options={{
                    strokeColor: '#ffffff', strokeOpacity: 0.85, strokeWeight: 5,
                    geodesic: true, clickable: false, zIndex: 1,
                  }} />
                  <Polyline path={mapPoints.path} options={{
                    strokeColor: '#1d4ed8', strokeOpacity: 0.95, strokeWeight: 3,
                    geodesic: true, clickable: false, zIndex: 2,
                  }} />
                </>
              )}

              {/* Origin */}
              {mapPoints.pickup && originIcon && (
                <Marker position={mapPoints.pickup}
                  icon={{
                    url: originIcon.url,
                    scaledSize: new window.google.maps.Size(originIcon.w, originIcon.h),
                    anchor: new window.google.maps.Point(originIcon.w / 2, originIcon.h),
                  }}
                  zIndex={500}
                />
              )}
              {/* Destination */}
              {mapPoints.delivery && destIcon && (
                <Marker position={mapPoints.delivery}
                  icon={{
                    url: destIcon.url,
                    scaledSize: new window.google.maps.Size(destIcon.w, destIcon.h),
                    anchor: new window.google.maps.Point(destIcon.w / 2, destIcon.h),
                  }}
                  zIndex={500}
                />
              )}

              {/* Numbered waypoints — one pin per sibling OT delivery, in the
                  optimised dispatch order. Selected leg is highlighted in
                  orange to match the leg-highlight polyline. Click any pin to
                  switch the bottom-strip detail to that OT. */}
              {siblingOTs.length > 1 && siblingOTs.map((ot, idx) => {
                const lat = Number(ot.deliveryLat);
                const lng = Number(ot.deliveryLng);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                const isSelected = Number(ot._id || ot.id) === Number(selectedOTId);
                const pin = buildWaypointPinIcon({ index: idx + 1, selected: isSelected });
                if (!pin) return null;
                return (
                  <Marker
                    key={`wp-${ot.id}`}
                    position={{ lat, lng }}
                    icon={{
                      url: pin.url,
                      scaledSize: new window.google.maps.Size(pin.w, pin.h),
                      anchor: new window.google.maps.Point(pin.w / 2, pin.h),
                    }}
                    title={`${idx + 1}. ${otLabel(ot)} — ${ot.deliveryName || ot.deliveryAddress || ''}`}
                    onClick={() => setSelectedOTId(ot._id || ot.id)}
                    zIndex={isSelected ? 800 : 600}
                  />
                );
              })}

              {/* LIVE vehicle position — red arrow that rotates with the
                  vehicle's course (matches the Frota/OrderMiniMap style).
                  The plate balloon is rendered as a SEPARATE marker so that
                  the rotation of the arrow doesn't tilt the text sideways. */}
              {vehiclePos && (
                <Marker
                  position={{ lat: vehiclePos.latitude, lng: vehiclePos.longitude }}
                  icon={buildLiveVehicleIcon(vehiclePos.course)}
                  title={`${order.vehiclePlate || ''} ${vehiclePos.simulated ? '(simulado)' : ''}`}
                  zIndex={1000}
                  // optimized:false forces re-rendering of the icon SVG every
                  // tick so the rotation actually updates. With the default
                  // (true), Google rasterises to a bitmap once and ignores
                  // subsequent rotation changes — the arrow appears stuck.
                  optimized={false}
                />
              )}
              {vehiclePos && order.vehiclePlate && (() => {
                const plate = buildPlateLabelIcon(order.vehiclePlate);
                if (!plate) return null;
                return (
                  <Marker
                    position={{ lat: vehiclePos.latitude, lng: vehiclePos.longitude }}
                    icon={{
                      url: plate.url,
                      scaledSize: new window.google.maps.Size(plate.w, plate.h),
                      // Anchor at the bottom-centre + extra 14px gap so the
                      // balloon's tail points to the arrow without overlapping.
                      anchor: new window.google.maps.Point(plate.w / 2, plate.h + 14),
                    }}
                    clickable={false}
                    zIndex={1001}
                  />
                );
              })()}
            </GoogleMap>
          ) : (
            <Box style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9' }}>
              <Typography style={{ color: '#64748b' }}>A carregar mapa…</Typography>
            </Box>
          )}
        </Box>

        {/* LEFT COLUMN — Driver, Vehicle, Métricas. Rendered visually BEFORE
            the map via `order: -1` even though it's a sibling that comes after
            in the DOM. The user explicitly asked for these three cards in the
            top-left corner. */}
        <Box className={classes.leftCol} style={{ order: -1 }}>
          {/* Driver */}
          <Box className={classes.bottomCard}>
            <Typography className={classes.cardTitle}>
              <PersonIcon style={{ fontSize: 13 }} /> Motorista
            </Typography>
            <Box className={classes.driverHeader}>
              <Box className={classes.driverAvatar}>{initials(driver.name)}</Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography className={classes.driverName} noWrap>{driver.name || '—'}</Typography>
                {driver.phone && <Typography className={classes.driverPhone}>📞 {driver.phone}</Typography>}
              </Box>
              {/* Chat icon — discrete circular button in the header so it
                  doesn't compete with Ligar/WhatsApp. Modern messaging-app feel. */}
              {order.driverId && (
                <Tooltip title="Chat com o motorista (mensagem in-app + notificação push)">
                  <IconButton
                    size="small"
                    onClick={() => setChatOpen(true)}
                    className={classes.driverChatBtn}
                  >
                    <ChatBubbleIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {driver.phone && (
              <Box className={classes.contactRow}>
                <Button size="small"
                  className={classes.contactBtn}
                  startIcon={<PhoneIcon style={{ fontSize: 14 }} />}
                  href={`tel:${phoneDigits}`}
                  style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}
                >
                  Ligar
                </Button>
                <Button size="small"
                  className={classes.contactBtn}
                  startIcon={<ChatBubbleIcon style={{ fontSize: 13 }} />}
                  href={`https://wa.me/${phoneDigits.replace(/^\+/, '')}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
                >
                  WhatsApp
                </Button>
              </Box>
            )}
            {!driver.phone && onContactDriver && (
              <Button size="small" fullWidth
                className={classes.contactBtn}
                onClick={() => onContactDriver(order)}
                startIcon={<ChatBubbleIcon style={{ fontSize: 13 }} />}
                style={{ background: 'linear-gradient(135deg, #64748b, #475569)' }}
              >
                Contactar
              </Button>
            )}
          </Box>

          {/* Vehicle */}
          <Box className={classes.bottomCard}>
            <Typography className={classes.cardTitle}>
              <LocalShippingIcon style={{ fontSize: 13 }} /> Veículo
            </Typography>
            <Box className={classes.vehicleRow}>
              <LocalShippingIcon style={{ fontSize: 22, color: '#3b82f6' }} />
              <Box style={{ flex: 1 }}>
                <Typography className={classes.vehiclePlate}>{order.vehiclePlate || '—'}</Typography>
                <Typography className={classes.vehicleSub}>
                  {vehiclePos
                    ? <>📍 {vehiclePos.simulated ? 'Simulado · ' : ''}{vehiclePos.fixTime
                        ? `Última posição: ${new Date(vehiclePos.fixTime).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`
                        : 'Live'}</>
                    : 'Sem posição GPS recente'}
                </Typography>
              </Box>
              {vehiclePos?.speed != null && (
                <Box style={{ textAlign: 'right' }}>
                  <Typography style={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>
                    {/* Real Traccar positions report speed in knots → km/h * 1.852.
                        Simulated positions emit speed already in km/h.
                        The previous heuristic (>5 ? *1.852 : *1) wrongly inflated
                        the speed of a parked vehicle whose last cached value was
                        e.g. 76 → 142 km/h. */}
                    {Math.round(Number(vehiclePos.speed) * (vehiclePos.simulated ? 1 : 1.852))}
                  </Typography>
                  <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>km/h</Typography>
                </Box>
              )}
            </Box>
            {/* When the vehicle has no live signal AND the route is active,
                offer the operator a quick "simulate" trigger so they can see
                the marker moving along the route. */}
            {!vehiclePos && order.assignedRouteId && order.status !== 'delivered' && order.status !== 'cancelled' && onStartSimulation && (
              <Box style={{ marginTop: 10, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                <Typography style={{ fontSize: '0.66rem', color: '#92400e', marginBottom: 6 }}>
                  Sem GPS live deste veículo. Para visualizar a rota a mexer, podes simular:
                </Typography>
                <Button size="small" variant="contained" fullWidth
                  onClick={() => onStartSimulation(order)}
                  startIcon={<PlayArrowIcon style={{ fontSize: 14 }} />}
                  style={{
                    fontSize: '0.7rem', fontWeight: 700, textTransform: 'none', borderRadius: 6,
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff',
                    boxShadow: 'none',
                  }}
                >
                  Iniciar simulação
                </Button>
              </Box>
            )}
          </Box>

          {/* ── Métricas da rota ──────────────────────────────
              Top-left grouping: kept next to Driver + Vehicle so the headline
              KPIs (km / duration) sit together at the top of the panel. */}
          {(routeMeta?.totalDistanceM || routeMeta?.totalDurationS || order.routeTotalDistanceM || order.distanceKm) && (() => {
            const distM = routeMeta?.totalDistanceM ?? order.routeTotalDistanceM ?? (order.distanceKm ? order.distanceKm * 1000 : null);
            const durS = routeMeta?.totalDurationS ?? order.routeTotalDurationS ?? (order.drivingTimeMin ? order.drivingTimeMin * 60 : null);
            const km = distM ? (distM / 1000).toFixed(1) : null;
            const h = durS ? Math.floor(durS / 3600) : 0;
            const m = durS ? Math.round((durS % 3600) / 60) : 0;
            const durLabel = durS ? (h > 0 ? `${h}h ${m}m` : `${m}m`) : null;
            return (
              <Box className={classes.bottomCard}>
                <Typography className={classes.cardTitle}>📊 Métricas da rota</Typography>
                <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {km && (
                    <Box style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: 8 }}>
                      <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Distância</Typography>
                      <Typography style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>{km}<span style={{ fontSize: '0.65rem', color: '#64748b', marginLeft: 4 }}>km</span></Typography>
                    </Box>
                  )}
                  {durLabel && (
                    <Box style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: 8 }}>
                      <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Duração</Typography>
                      <Typography style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>{durLabel}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })()}
        </Box>{/* end of leftCol */}

        {/* RIGHT COLUMN — sibling OTs, order details, tacho hint, state-op
            timeline. Stays scrollable independently from the left column so a
            long order details block doesn't push the driver card out of view. */}
        <Box className={classes.rightCol}>
          {/* OTs of this route */}
          {siblingOTs.length > 1 && (
            <Box className={classes.bottomCard}>
              <Typography className={classes.cardTitle}>
                <RoomIcon style={{ fontSize: 13 }} /> Ordens desta rota ({siblingOTs.length})
              </Typography>
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {siblingOTs.map((ot, idx) => {
                  const cfg = STATUS_CFG[ot.status] || STATUS_CFG.pending;
                  const isSelected = Number(ot._id || ot.id) === Number(selectedOTId);
                  const km = legKmByOTId.get(Number(ot._id || ot.id));
                  const kmStr = (km != null && Number.isFinite(km) && km > 0)
                    ? `${km.toFixed(1)} km`
                    : null;
                  return (
                    <Box key={ot._id || ot.id}
                      className={`${classes.otRow} ${isSelected ? classes.otRowActive : ''}`}
                      onClick={() => setSelectedOTId(ot._id || ot.id)}
                    >
                      <Box className={classes.otBadge} style={{ background: cfg.bg, color: cfg.color }}>
                        {idx + 1}
                      </Box>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography className={classes.otName} noWrap>
                          {otLabel(ot)} · {ot.deliveryName || ot.deliveryAddress || '—'}
                        </Typography>
                        <Typography className={classes.otSub}>
                          {cfg.label}
                          {kmStr ? ` · ${idx === 0 ? 'desde origem' : 'desde paragem anterior'}: ${kmStr}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', marginTop: 6, fontStyle: 'italic' }}>
                Clica numa ordem para ver o troço destacado no mapa e os detalhes em baixo.
              </Typography>
            </Box>
          )}

          {/* "Detalhes da ordem" was moved to the bottom strip (next to the
              history) so that clicking an OT in the list above swaps the leg
              details without taking real estate from the right column.
              "Tacógrafo" hint was dropped from this column too — the duration
              card on the planner already deep-links to the tacho timeline. */}

          {/* ── Linha temporal de execução ─────────────────── */}
          {(() => {
            const fmt = (d) => d ? new Date(d).toLocaleString('pt-PT', {
              day: '2-digit', month: '2-digit', year: '2-digit',
              hour: '2-digit', minute: '2-digit',
            }) : null;
            // Build the execution timeline (same structure as the inline
            // expand panel on the OT row). Each step has: label, time, color,
            // icon, and a `done` flag that drives the visual state.
            const steps = [];
            steps.push({
              key: 'created', label: 'Criada', time: fmt(order.createdAt),
              color: '#64748b', icon: '📝', done: !!order.createdAt,
            });
            if (order.pickupWindowFrom) {
              const txt = `${fmt(order.pickupWindowFrom)}${order.pickupWindowTo ? ` → ${new Date(order.pickupWindowTo).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}` : ''}`;
              steps.push({ key: 'pickwin', label: 'Janela recolha', time: txt, color: '#22c55e', icon: '🟢', done: false });
            }
            if (order.deliveryWindowFrom) {
              const txt = `${fmt(order.deliveryWindowFrom)}${order.deliveryWindowTo ? ` → ${new Date(order.deliveryWindowTo).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}` : ''}`;
              steps.push({ key: 'delwin', label: 'Janela entrega', time: txt, color: '#ef4444', icon: '🔴', done: false });
            }
            if (order.actualStartAt) {
              steps.push({ key: 'started', label: 'Início real', time: fmt(order.actualStartAt), color: '#f59e0b', icon: '▶', done: true });
            }
            if (order.estimatedArrival && !order.deliveredAt) {
              const isLate = order.deliveryWindowTo && new Date(order.estimatedArrival).getTime() > new Date(order.deliveryWindowTo).getTime() + 5 * 60_000;
              steps.push({
                key: 'eta', label: isLate ? 'ETA · atrasado' : 'Chegada prevista',
                time: fmt(order.estimatedArrival), color: isLate ? '#dc2626' : '#3b82f6', icon: '⏱', done: false,
                extra: order.unloadingTime > 0 ? `+${order.unloadingTime}min descarga` : null,
              });
            }
            if (order.deliveredAt) {
              steps.push({ key: 'delivered', label: 'Entregue', time: fmt(order.deliveredAt), color: '#16a34a', icon: '✔', done: true });
            }
            if (order.status === 'cancelled') {
              steps.push({ key: 'cancelled', label: 'Cancelada', time: '—', color: '#ef4444', icon: '✕', done: true });
            }
            return (
              <Box className={classes.bottomCard}>
                <Typography className={classes.cardTitle}>
                  ⏱ Linha temporal de execução
                </Typography>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {steps.map((s, i) => (
                    <Box key={s.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '4px 0' }}>
                      <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box style={{
                          width: 22, height: 22, borderRadius: '50%',
                          background: s.done ? s.color : '#fff',
                          border: `2px solid ${s.color}`,
                          color: s.done ? '#fff' : s.color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 800, flexShrink: 0,
                        }}>{s.icon}</Box>
                        {i < steps.length - 1 && (
                          <Box style={{ width: 2, flex: 1, minHeight: 14, background: '#e2e8f0', marginTop: 2 }} />
                        )}
                      </Box>
                      <Box style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
                        <Typography style={{ fontSize: '0.62rem', fontWeight: 700, color: s.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {s.label}
                        </Typography>
                        <Typography style={{ fontSize: '0.7rem', color: '#0f172a', fontWeight: 600 }}>
                          {s.time || '—'}
                        </Typography>
                        {s.extra && (
                          <Typography style={{
                            display: 'inline-block',
                            fontSize: '0.58rem', fontWeight: 700,
                            background: 'rgba(245,158,11,0.12)', color: '#d97706',
                            padding: '1px 6px', borderRadius: 8, marginTop: 2,
                          }}>{s.extra}</Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })()}
        </Box>{/* end of rightCol */}
        </Box>{/* end of bodyTop */}

        {/* BOTTOM STRIP — split in two halves: history (left) and selected-leg
            details (right). Both halves scroll independently inside the strip
            so the operator never loses sight of either. Top edge is draggable
            so the operator can collapse the strip and give the map more room. */}
        <Box className={classes.historyStrip} style={{ height: stripHeight }}>
          <Box className={classes.stripDragHandle}
            onMouseDown={onDragStart}
            onDoubleClick={toggleStrip}
            title="Arrasta para redimensionar · clique-duplo para alternar"
          >
            <Box className={classes.stripDragGrip} />
          </Box>
          {stripCollapsed ? (
            <Box className={classes.stripCollapsedNote} onClick={toggleStrip} style={{ cursor: 'pointer' }}>
              ▲ Histórico e detalhes do troço · clique para abrir
            </Box>
          ) : (
          <Box className={classes.stripBody}>
          <Box className={classes.historyHalf}>
            <Box className={classes.historyHeader}>
              <HistoryIcon style={{ fontSize: 14, color: '#475569' }} />
              <Typography style={{ fontSize: '0.7rem', fontWeight: 800, color: '#0f172a' }}>
                Histórico
              </Typography>
              <Chip size="small" label={historyEvents.length}
                style={{ height: 18, fontSize: '0.58rem', background: '#e2e8f0', color: '#475569', fontWeight: 700 }}
              />
              {historyLoading && (
                <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>· a carregar…</Typography>
              )}
            </Box>
            <Box className={classes.historyPane}>
            {historyEvents.length === 0 && !historyLoading && (
              <Typography style={{ fontSize: '0.7rem', color: '#94a3b8', textAlign: 'center', padding: 14 }}>
                Sem eventos registados.
              </Typography>
            )}
            {historyEvents.length > 0 && (
              <table className={classes.historyTable}>
                <thead>
                  <tr style={{ background: '#fafbfc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '5px 10px', fontSize: '0.56rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', width: 110 }}>Quando</th>
                    <th style={{ textAlign: 'left', padding: '5px 10px', fontSize: '0.56rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', width: 130 }}>Evento</th>
                    <th style={{ textAlign: 'left', padding: '5px 10px', fontSize: '0.56rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', width: 130 }}>Estado</th>
                    <th style={{ textAlign: 'left', padding: '5px 10px', fontSize: '0.56rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {[...historyEvents].reverse().map((ev, i) => {
                    const cfg = {
                      created:          { label: 'Criada',                color: '#64748b', icon: '📝' },
                      vehicle_assigned: { label: 'Veículo atribuído',     color: '#3b82f6', icon: '🚛' },
                      route_linked:     { label: 'Rota associada',        color: '#6366f1', icon: '🔗' },
                      started:          { label: 'Iniciada',              color: '#f59e0b', icon: '▶' },
                      delivered:        { label: 'Entregue',              color: '#16a34a', icon: '✔' },
                      cancelled:        { label: 'Cancelada',             color: '#ef4444', icon: '✕' },
                      re_dispatched:    { label: 'Re-despachada',         color: '#8b5cf6', icon: '🔄' },
                    }[ev.eventType] || { label: ev.eventType, color: '#475569', icon: '·' };
                    return (
                      <tr key={ev.id || i} className={classes.historyRow}>
                        <td style={{ color: '#64748b' }}>
                          {ev.createdAt ? new Date(ev.createdAt).toLocaleString('pt-PT', {
                            day: '2-digit', month: '2-digit', year: '2-digit',
                            hour: '2-digit', minute: '2-digit',
                          }) : '—'}
                        </td>
                        <td>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 6px', borderRadius: 4,
                            background: `${cfg.color}15`, color: cfg.color,
                            fontWeight: 700, fontSize: '0.62rem',
                          }}>
                            <span>{cfg.icon}</span> {cfg.label}
                          </span>
                        </td>
                        <td style={{ color: '#475569' }}>
                          {ev.fromStatus && ev.toStatus
                            ? <span>{ev.fromStatus} → <b>{ev.toStatus}</b></span>
                            : (ev.toStatus || '—')}
                        </td>
                        <td style={{ color: '#0f172a' }}>{ev.notes || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            </Box>{/* end historyPane */}
          </Box>{/* end historyHalf */}

          {/* RIGHT HALF — currently selected OT/leg details. Empty placeholder
              when nothing is selected; otherwise mirrors the per-OT details
              that used to live in the right column. */}
          <Box className={classes.legHalf}>
            <Box className={classes.legHeader}>
              <RoomIcon style={{ fontSize: 14, color: '#475569' }} />
              <Typography style={{ fontSize: '0.7rem', fontWeight: 800, color: '#0f172a' }}>
                Detalhes do troço
              </Typography>
              {selectedOT && (
                <Chip size="small" label={otLabel(selectedOT)}
                  style={{ height: 18, fontSize: '0.58rem', background: '#eef2ff', color: '#4338ca', fontWeight: 700 }}
                />
              )}
            </Box>
            <Box className={classes.legPane}>
              {(() => {
                if (!selectedOT) return (
                  <Typography style={{ fontSize: '0.7rem', color: '#94a3b8', textAlign: 'center', padding: 14 }}>
                    Selecciona uma ordem na coluna direita para ver o seu detalhe.
                  </Typography>
                );
                const fmtDateTime = (d) => d ? new Date(d).toLocaleString('pt-PT', {
                  day: '2-digit', month: '2-digit', year: '2-digit',
                  hour: '2-digit', minute: '2-digit',
                }) : null;
                const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                }) : null;
                const rows = [];
                const pickup = selectedOT.pickupName || selectedOT.pickupAddress;
                const delivery = selectedOT.deliveryName || selectedOT.deliveryAddress;
                if (pickup) {
                  const subParts = [];
                  if (selectedOT.pickupAddress && selectedOT.pickupAddress !== selectedOT.pickupName) subParts.push(selectedOT.pickupAddress);
                  if (selectedOT.pickupContact || selectedOT.pickupPhone) {
                    subParts.push(`👤 ${selectedOT.pickupContact || '—'}${selectedOT.pickupPhone ? ` · ${selectedOT.pickupPhone}` : ''}`);
                  }
                  rows.push({ label: 'Origem', value: pickup, sub: subParts.join(' · '), color: '#16a34a' });
                }
                if (delivery) {
                  const subParts = [];
                  if (selectedOT.deliveryAddress && selectedOT.deliveryAddress !== selectedOT.deliveryName) subParts.push(selectedOT.deliveryAddress);
                  if (selectedOT.deliveryContact || selectedOT.deliveryPhone) {
                    subParts.push(`👤 ${selectedOT.deliveryContact || '—'}${selectedOT.deliveryPhone ? ` · ${selectedOT.deliveryPhone}` : ''}`);
                  }
                  rows.push({ label: 'Destino', value: delivery, sub: subParts.join(' · '), color: '#dc2626' });
                }
                const pickupWindow = (selectedOT.pickupWindowFrom || selectedOT.pickupWindowTo)
                  ? `${fmtDateTime(selectedOT.pickupWindowFrom) || '—'}${selectedOT.pickupWindowTo ? ` → ${fmtDateTime(selectedOT.pickupWindowTo)}` : ''}`
                  : null;
                const deliveryWindow = (selectedOT.deliveryWindowFrom || selectedOT.deliveryWindowTo)
                  ? `${fmtDateTime(selectedOT.deliveryWindowFrom) || '—'}${fmtDateTime(selectedOT.deliveryWindowTo) ? ` → ${fmtDateTime(selectedOT.deliveryWindowTo)}` : ''}`
                  : null;
                if (pickupWindow) rows.push({ label: 'Janela recolha', value: pickupWindow });
                if (deliveryWindow) rows.push({ label: 'Janela entrega', value: deliveryWindow });
                if (selectedOT.serviceDate) rows.push({ label: 'Data do serviço', value: fmtDate(selectedOT.serviceDate) });
                if (selectedOT.slaHours) rows.push({ label: 'SLA', value: `${selectedOT.slaHours}h`, color: '#dc2626' });
                if (selectedOT.unloadingTime) rows.push({ label: 'Tempo serviço', value: `${selectedOT.unloadingTime} min` });
                const cargoBits = [];
                if (selectedOT.cargoDescription) cargoBits.push(selectedOT.cargoDescription);
                if (selectedOT.cargoWeight) cargoBits.push(`${selectedOT.cargoWeight}kg`);
                if (selectedOT.cargoVolume) cargoBits.push(`${selectedOT.cargoVolume}m³`);
                if (selectedOT.cargoPallets) cargoBits.push(`${selectedOT.cargoPallets} paletes`);
                if (cargoBits.length) rows.push({ label: 'Carga', value: cargoBits.join(' · ') });
                if (selectedOT.externalRef) rows.push({ label: 'Ref. externa', value: selectedOT.externalRef });
                if (selectedOT.actualStartAt) rows.push({ label: 'Início real', value: fmtDateTime(selectedOT.actualStartAt), color: '#f59e0b' });
                if (selectedOT.estimatedArrival) rows.push({ label: 'ETA', value: fmtDateTime(selectedOT.estimatedArrival) });
                if (selectedOT.deliveredAt) rows.push({ label: 'Entregue em', value: fmtDateTime(selectedOT.deliveredAt), color: '#16a34a' });
                if (rows.length === 0) return (
                  <Typography style={{ fontSize: '0.7rem', color: '#94a3b8', textAlign: 'center', padding: 14 }}>
                    Sem detalhes para mostrar.
                  </Typography>
                );
                return (
                  <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {rows.map((r, i) => (
                      <Box key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <Typography style={{
                          fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8',
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          minWidth: 92, flexShrink: 0, paddingTop: 2,
                        }}>
                          {r.label}
                        </Typography>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Typography style={{
                            fontSize: '0.74rem', color: r.color || '#0f172a', fontWeight: 600,
                            wordBreak: 'break-word',
                          }}>
                            {r.value}
                          </Typography>
                          {r.sub && (
                            <Typography style={{ fontSize: '0.62rem', color: '#64748b', marginTop: 1 }}>
                              {r.sub}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                    {selectedOT.notes && (
                      <Box style={{
                        marginTop: 6, padding: '8px 10px',
                        background: '#fffbeb', borderRadius: 6, border: '1px solid #fde68a',
                      }}>
                        <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                          📝 Notas
                        </Typography>
                        <Typography style={{ fontSize: '0.72rem', color: '#78350f', lineHeight: 1.45 }}>
                          {selectedOT.notes}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Box>
          </Box>
          </Box>
          )}
        </Box>
      </Box>

      {/* FOOTER ─────────────────────────────────────────────── */}
      <Box className={classes.footer}>
        <Box style={{ flex: 1 }} />
        {!isFinal && status === 'assigned' && order.assignedRouteId && (
          <Button size="small" variant="contained" className={classes.actionBtn}
            startIcon={<PlayArrowIcon style={{ fontSize: 16 }} />}
            onClick={() => onStatusChange && onStatusChange(order, 'in_progress')}
            style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', boxShadow: '0 2px 6px rgba(34,197,94,0.3)' }}
          >
            Iniciar
          </Button>
        )}
        {/* Manual "Concluir" was removed from the admin side: under the
            per-OT delivery model the route auto-completes on the server
            when the driver saves the report for the last in-transit OT.
            If something needs to close the route abnormally, the driver
            uses "Reportar avaria" in the app (which frees up unfinished
            OTs back to dispatch) — there's no equivalent admin button
            because that would skip per-OT reports and lose the audit. */}
        {!isFinal && (status === 'assigned' || status === 'in_transit') && (
          <Tooltip title="Cancelar e libertar a rota (rotas consolidadas afectam todas as ordens)">
            <Button size="small" className={classes.actionBtn}
              startIcon={<CancelIcon style={{ fontSize: 16 }} />}
              onClick={() => setCancelConfirmOpen(true)}
              style={{ color: '#ef4444', border: '1px solid #fecaca' }}
            >
              Cancelar
            </Button>
          </Tooltip>
        )}
        {!isFinal && order.assignedRouteId && onAddOrderToRoute && (
          <Tooltip title="Adicionar uma ordem pendente a esta rota — re-otimiza e abre o planner para guardar e reenviar">
            <Button size="small" variant="outlined" className={classes.actionBtn}
              startIcon={<RoomIcon style={{ fontSize: 16 }} />}
              onClick={() => setAddOrderOpen(true)}
              style={{ borderColor: '#6366f1', color: '#4338ca', background: '#eef2ff' }}
            >
              Adicionar ordem
            </Button>
          </Tooltip>
        )}
        {!isFinal && order.assignedRouteId && onEditRoute && (
          <Button size="small" variant="contained" className={classes.actionBtn}
            startIcon={<EditIcon style={{ fontSize: 16 }} />}
            onClick={() => onEditRoute(order)}
            style={{ background: '#0f172a', color: '#fff' }}
          >
            Editar rota
          </Button>
        )}
      </Box>

      {/* CANCEL CONFIRMATION ──────────────────────────────────
          Própria caixa de confirmação (em vez do window.confirm do browser).
          Quando a rota é consolidada, lista as ordens irmãs que vão também
          ser libertadas para o operador ver o impacto. */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        PaperProps={{ style: { borderRadius: 14, overflow: 'hidden', maxWidth: 460, width: '90vw' } }}
      >
        {/* Cabeçalho avermelhado para sinalizar acção destrutiva */}
        <Box style={{
          padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12,
          background: 'linear-gradient(135deg, #fef2f2, #fecaca)',
          borderBottom: '1px solid #fca5a5',
        }}>
          <Box style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(239,68,68,0.3)',
          }}>
            <CancelIcon style={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography style={{ fontSize: '0.95rem', fontWeight: 800, color: '#7f1d1d' }}>
              Cancelar Ordem de Serviço
            </Typography>
            <Typography style={{ fontSize: '0.68rem', color: '#991b1b', marginTop: 1 }}>
              {siblingOTs.length > 1
                ? `Esta acção cancela ${siblingOTs.length} ordens partilhando a mesma rota`
                : 'A rota associada volta a estado rascunho'}
            </Typography>
          </Box>
        </Box>

        {/* Corpo */}
        <Box style={{ padding: '16px 20px' }}>
          <Typography style={{ fontSize: '0.82rem', color: '#334155', lineHeight: 1.55 }}>
            Tens a certeza que queres cancelar a ordem <b>{otLabel(order)}</b>?
            {siblingOTs.length > 1 && (
              <> Esta ordem faz parte de uma <b>rota consolidada</b>: cancelar liberta também as outras ordens da mesma rota e a rota volta a <b>draft</b>.</>
            )}
            {siblingOTs.length <= 1 && (
              <> A rota volta a estado <b>draft</b> e a ordem fica <b>pendente</b>.</>
            )}
          </Typography>

          {siblingOTs.length > 1 && (
            <Box style={{
              marginTop: 12, padding: 10,
              background: '#fafbfc', border: '1px solid #e2e8f0', borderRadius: 8,
            }}>
              <Typography style={{ fontSize: '0.6rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                Ordens afectadas
              </Typography>
              {siblingOTs.map(o => (
                <Box key={o._id || o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <Box style={{
                    width: 18, height: 18, borderRadius: 4,
                    background: STATUS_CFG[o.status]?.bg || '#f1f5f9',
                    color: STATUS_CFG[o.status]?.color || '#64748b',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, fontWeight: 800,
                  }}>
                    {(o._id || o.id) === (order._id || order.id) ? '➤' : '·'}
                  </Box>
                  <Typography style={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f172a' }}>
                    {otLabel(o)}
                  </Typography>
                  <Typography style={{ fontSize: '0.68rem', color: '#64748b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o.deliveryName || o.deliveryAddress || ''}
                  </Typography>
                  <Chip size="small" label={STATUS_CFG[o.status]?.label || o.status}
                    style={{
                      height: 18, fontSize: '0.58rem', fontWeight: 700,
                      background: STATUS_CFG[o.status]?.bg, color: STATUS_CFG[o.status]?.color,
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}

          <Box style={{
            marginTop: 12, padding: '8px 10px',
            background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8,
          }}>
            <Typography style={{ fontSize: '0.7rem', color: '#92400e' }}>
              ⚠ Esta acção <b>não pode ser desfeita</b>. As ordens canceladas terão de ser re-atribuídas e a rota re-otimizada.
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box style={{
          padding: '12px 20px', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          background: '#fafbfc',
        }}>
          <Button size="small" onClick={() => setCancelConfirmOpen(false)}
            style={{ fontSize: '0.78rem', textTransform: 'none', color: '#64748b', borderRadius: 8, padding: '7px 14px' }}>
            Voltar
          </Button>
          <Button size="small" variant="contained"
            startIcon={<CancelIcon style={{ fontSize: 16 }} />}
            onClick={() => {
              setCancelConfirmOpen(false);
              onStatusChange && onStatusChange(order, 'cancelled');
            }}
            style={{
              fontSize: '0.78rem', fontWeight: 700, textTransform: 'none',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff',
              borderRadius: 8, padding: '7px 18px',
              boxShadow: '0 2px 6px rgba(239,68,68,0.3)',
            }}>
            {siblingOTs.length > 1 ? `Cancelar ${siblingOTs.length} ordens` : 'Cancelar ordem'}
          </Button>
        </Box>
      </Dialog>

      {/* ADD ORDER TO ROUTE PICKER ─────────────────────────────
          Lists pending OTs the operator can attach to this route. Same
          serviceType only — mixing assistance with delivery routes would
          break the driver-home / fuel-yard origin rules. */}
      <Dialog
        open={addOrderOpen}
        onClose={() => setAddOrderOpen(false)}
        PaperProps={{ style: { borderRadius: 14, overflow: 'hidden', maxWidth: 540, width: '92vw' } }}
      >
        <Box style={{
          padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10,
          background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
          borderBottom: '1px solid #c7d2fe',
        }}>
          <Box style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(99,102,241,0.3)',
          }}>
            <RoomIcon style={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box style={{ flex: 1 }}>
            <Typography style={{ fontSize: '0.92rem', fontWeight: 800, color: '#3730a3' }}>
              Adicionar ordem à rota
            </Typography>
            <Typography style={{ fontSize: '0.66rem', color: '#4338ca' }}>
              Selecciona uma ordem pendente · será atribuída ao mesmo veículo/motorista e a rota é re-otimizada antes de reenviar.
            </Typography>
          </Box>
        </Box>
        <Box style={{ padding: 14 }}>
          <Box style={{ marginBottom: 10 }}>
            <input
              autoFocus
              placeholder="Pesquisar por OT, cliente, morada..."
              value={addOrderSearch}
              onChange={(e) => setAddOrderSearch(e.target.value)}
              style={{
                width: '100%', padding: '8px 10px',
                fontSize: '0.78rem', borderRadius: 8,
                border: '1px solid #e2e8f0', outline: 'none',
                background: '#f8fafc',
              }}
            />
          </Box>
          {(() => {
            const sourceType = order?.serviceType || 'delivery';
            const siblingIds = new Set(siblingOTs.map(o => Number(o._id || o.id)));
            // Route's driver — used to filter candidates whose required
            // skills aren't covered by this driver. Without this filter
            // the operator could attach an OT the driver isn't qualified
            // to handle and only find out at runtime.
            const routeDriver = (drivers || []).find(d => Number(d.id) === Number(order?.driverId));
            const driverSkills = Array.isArray(routeDriver?.skills) ? routeDriver.skills : [];
            // Same-day filter — assistance/fuel routes are usually one-day
            // operations. Compare by YYYY-MM-DD so timezones don't bite.
            const dayOf = (d) => d ? new Date(d).toISOString().slice(0, 10) : null;
            const routeDay = dayOf(order?.serviceDate || order?.deliveryWindowFrom);
            const candidates = (allOrders || []).filter(o => {
              if (siblingIds.has(Number(o._id || o.id))) return false;
              if (o.status !== 'pending') return false;
              if ((o.serviceType || 'delivery') !== sourceType) return false;
              if (!o.deliveryLat || !o.deliveryLng) return false;
              // Same-day requirement — accept candidates that match the
              // route's service day OR have no date set (operator can
              // override). Different explicit days are filtered out so
              // the operator doesn't accidentally cross-day a route.
              const candDay = dayOf(o.serviceDate || o.deliveryWindowFrom);
              if (routeDay && candDay && routeDay !== candDay) return false;
              const q = addOrderSearch.trim().toLowerCase();
              if (!q) return true;
              return [o.orderNumber, o.customRef, o.deliveryName, o.deliveryAddress, o.pickupName, o.pickupAddress]
                .some(v => (v || '').toLowerCase().includes(q));
            });
            // Skills compatibility per candidate — used to flag (not hide)
            // OTs the route's driver isn't qualified for. The operator
            // sees a warning but can still pick if they accept the risk.
            const skillsOf = (o) => {
              const req = Array.isArray(o.requiredSkills) ? o.requiredSkills : [];
              const missing = req.filter(s => !driverSkills.includes(s));
              return { req, missing, ok: missing.length === 0 };
            };
            if (candidates.length === 0) return (
              <Box style={{ padding: '24px 12px', textAlign: 'center' }}>
                <Typography style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                  {addOrderSearch
                    ? 'Sem resultados.'
                    : `Sem ordens pendentes do tipo "${sourceType === 'assistance' ? 'assistência' : sourceType === 'fuel' ? 'combustível' : 'entrega'}".`}
                </Typography>
              </Box>
            );
            return (
              <Box style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {candidates.slice(0, 50).map(o => {
                  const sk = skillsOf(o);
                  const candDay = dayOf(o.serviceDate || o.deliveryWindowFrom);
                  return (
                  <Box key={o._id || o.id}
                    onClick={() => {
                      onAddOrderToRoute && onAddOrderToRoute(order, o);
                      setAddOrderOpen(false);
                      setAddOrderSearch('');
                    }}
                    style={{
                      padding: '10px 12px', borderRadius: 10,
                      border: `1px solid ${sk.ok ? '#e2e8f0' : '#fde68a'}`,
                      background: sk.ok ? '#fff' : '#fffbeb',
                      cursor: 'pointer',
                      transition: 'background 0.12s, border-color 0.12s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = sk.ok ? '#eef2ff' : '#fef3c7'; e.currentTarget.style.borderColor = sk.ok ? '#c7d2fe' : '#fcd34d'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = sk.ok ? '#fff' : '#fffbeb'; e.currentTarget.style.borderColor = sk.ok ? '#e2e8f0' : '#fde68a'; }}
                  >
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Typography style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a' }}>
                        {otLabel(o)}
                      </Typography>
                      <Chip size="small" label="Pendente"
                        style={{ height: 18, fontSize: '0.58rem', background: '#f1f5f9', color: '#475569', fontWeight: 700 }} />
                      {candDay && (
                        <Chip size="small" label={`📅 ${new Date(candDay).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}`}
                          style={{ height: 18, fontSize: '0.58rem', background: '#dbeafe', color: '#1e40af', fontWeight: 700 }} />
                      )}
                      {!candDay && (
                        <Chip size="small" label="📅 sem data"
                          style={{ height: 18, fontSize: '0.58rem', background: '#f1f5f9', color: '#94a3b8', fontWeight: 700 }} />
                      )}
                      {o.slaHours && (
                        <Chip size="small" label={`SLA ${o.slaHours}h`}
                          style={{ height: 18, fontSize: '0.58rem', background: '#fef2f2', color: '#dc2626', fontWeight: 700 }} />
                      )}
                      {!sk.ok && (
                        <Chip size="small" label={`⚠ skills em falta: ${sk.missing.join('+')}`}
                          style={{ height: 18, fontSize: '0.58rem', background: '#fef3c7', color: '#b45309', fontWeight: 700 }} />
                      )}
                    </Box>
                    <Typography style={{ fontSize: '0.72rem', color: '#0f172a', marginTop: 3 }}>
                      📍 {o.deliveryName || o.deliveryAddress || '—'}
                    </Typography>
                    {(o.deliveryAddress && o.deliveryAddress !== o.deliveryName) && (
                      <Typography style={{ fontSize: '0.62rem', color: '#64748b', marginTop: 1 }}>
                        {o.deliveryAddress}
                      </Typography>
                    )}
                    {o.deliveryWindowFrom && (
                      <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', marginTop: 2 }}>
                        Janela: {new Date(o.deliveryWindowFrom).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    )}
                  </Box>
                  );
                })}
                {candidates.length > 50 && (
                  <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', textAlign: 'center', marginTop: 4 }}>
                    {candidates.length - 50} resultados adicionais — refina a pesquisa.
                  </Typography>
                )}
              </Box>
            );
          })()}
        </Box>
        <Box style={{ padding: '10px 14px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => { setAddOrderOpen(false); setAddOrderSearch(''); }}
            style={{ fontSize: '0.74rem', textTransform: 'none', color: '#64748b' }}>
            Fechar
          </Button>
        </Box>
      </Dialog>

      {/* DRIVER CHAT DIALOG — sibling overlay so it stacks above the live
          panel. Opens from the "Chat Motorista" button in the Driver card. */}
      <ChatDriverDialog
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        driverId={order?.driverId}
        driverName={driver?.name}
        driverPhone={driver?.phone}
      />
    </Dialog>
  );
}

export default LiveRoutePanel;
